import { ReactElement, useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  MarketButton,
  MarketContentCard,
  MarketDialog,
  MarketDropdown,
  MarketHeader,
  MarketList,
  MarketPopover,
  MarketRow,
  MarketStepper,
} from '@market/react';
import { Customer, Discount, view } from '@squareup/sq-addons-web-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { MarketStepperCustomEvent } from '@market/web-components/loader';
import qs from 'qs';
import { ReactComponent as ArrowLeft } from '@market/icons/assets/svg/arrow-left.svg';
import { ReactComponent as EllipsisHorizontal } from '@market/icons/assets/svg/ellipsis-horizontal.svg';

import {
  RequestContext,
  addCustomerToSaleStateSelector,
  customerContextSelector,
  customerSelector,
  isCustomerAddedToSaleSelector,
  pointBalanceSelector,
  removeCustomerFromSaleStateSelector,
} from 'store/customer';
import * as customerActions from 'store/customer/actions';
import {
  addDiscountStateSelector,
  appliedDiscountsSelector,
  availableDiscountsSelector,
  clearDiscountsStateSelector,
  fetchDiscountsStateSelector,
  removeDiscountStateSelector,
  unavailableDiscountsSelector,
  updatedDiscountsSelector,
} from 'store/discounts';
import * as discountsActions from 'store/discounts/actions';
import LoadingScreen from './Loading';
import ConnectionError from 'screens/ConnectionError';
import { removeDiscountNamePrefix } from 'utils/discount';
import { getCustomerDisplayName } from 'utils/customer';

type RedeemableDiscountCardProps = {
  availableDiscounts: Discount[];
  appliedDiscounts: Discount[];
  isCustomerAddedToSale: boolean;
  rewardTierId: string;
  customer?: Customer;
};


const RedeemableDiscountCard = ({
  availableDiscounts,
  appliedDiscounts,
  isCustomerAddedToSale,
  rewardTierId,
  customer,
}: RedeemableDiscountCardProps) => {
  const dispatch = useDispatch();

  const discount = availableDiscounts[0] || appliedDiscounts[0];
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: discount.amountMoney.currency,
  });
  const availableDiscountsCount = availableDiscounts.length;
  const appliedDiscountsCount = appliedDiscounts.length;
  let totalDiscountAmount = 0;

  for ( let i = 0; i < appliedDiscountsCount; i++) {
    totalDiscountAmount += appliedDiscounts[i]?.appliedMoney?.amount ?? 0;
  }

  const addDiscount = () => {
    const availableDiscount = availableDiscounts[0];

    if (availableDiscount) {
      if (!isCustomerAddedToSale && customer) {
        dispatch(customerActions.addCustomerToSale(customer));
      }
      dispatch(discountsActions.addDiscount(availableDiscount));
    } else {
      throw new Error(
        'Unexpected application state. There should be an available discount to redeem.'
      );
    }
  };

  const removeDiscount = () => {
    const appliedDiscount = appliedDiscounts[0];

    if (appliedDiscount) {
      if (appliedDiscount.uid) {
        dispatch(discountsActions.removeDiscount(appliedDiscount));
      } else {
        throw new Error('discount uid cannot be undefined.');
      }
    } else {
      throw new Error(
        'Unexpected application state. There should an applied discount to unredeem.'
      );
    }
  };

  const onStepperChange = (
    e: MarketStepperCustomEvent<{ previousValue: number; value: number }>
  ) => {
    const { value, previousValue } = e.detail;
    if (value - previousValue > 0) {
      addDiscount();
    } else {
      removeDiscount();
    }
  };

  let control: ReactElement;

  control = (
    <MarketButton
      slot="control"
      rank="secondary"
      disabled={true}
      onClick={() => addDiscount()}
    >
      Redeem
    </MarketButton>
  );

  return (
    <MarketContentCard
      className={`addons-available-rewards-content-card ${
        appliedDiscounts.length > 0
          ? 'addons-available-rewards-content-card-redeemed'
          : 'addons-available-rewards-content-card-unredeemed'
      }`}
    >
      <MarketRow data-testid={`${rewardTierId}-card`}>
        <label slot="label" className="addons-market-action-card-label">
          {removeDiscountNamePrefix(discount.name ? discount.name : '')}
        </label>
        <div slot="subtext" className="addons-market-action-card-subtext">
          {/* Jude TODO: Add in support for percentage-based discounts */}
          {currencyFormatter.format(totalDiscountAmount / 100)}
        </div>
        {control}
      </MarketRow>
    </MarketContentCard>
  );
};

type BackWarningModalProps = {
  onDismiss: () => void;
};

const BackWarningModal = ({ onDismiss }: BackWarningModalProps) => {
  return (
    <MarketDialog trapFocus data-testid="back-warning-modal">
      <MarketHeader style={{ marginBottom: '10px' }}>
        <h2>Unable to go back</h2>
      </MarketHeader>
      <main style={{ overflow: 'hidden' }}>
        <p style={{ marginBottom: '30px' }}>
          Please remove the customer from the sale before going back. Dismiss
          this screen and press "Done" if you are finished.
        </p>

        <MarketButton
          data-testid="back-warning-modal-dismiss-btn"
          rank="primary"
          role="button"
          onClick={onDismiss}
          style={{ width: '100%' }}
        >
          Dismiss
        </MarketButton>
      </main>
    </MarketDialog>
  );
};

type RedeemableDiscounts = {
  [rewardTierId: string]: {
    availableDiscounts: Discount[];
    appliedDiscounts: Discount[];
  };
};

const CustomerScreen = () => {
  const dispatch = useDispatch();

  const customer = useSelector(customerSelector) as Customer;
  const pointBalance = useSelector(pointBalanceSelector);
  const isCustomerAddedToSale = useSelector(isCustomerAddedToSaleSelector);
  const addCustomerToSaleState = useSelector(addCustomerToSaleStateSelector);
  const removeCustomerFromSaleState = useSelector(
    removeCustomerFromSaleStateSelector
  );
  const customerContext = useSelector(customerContextSelector);

  const availableDiscounts = useSelector(availableDiscountsSelector);
  const appliedDiscounts = useSelector(appliedDiscountsSelector);
  const unavailableDiscounts = useSelector(unavailableDiscountsSelector);
  const updatedDiscounts = useSelector(updatedDiscountsSelector);

  const [redeemableDiscounts, setRedeemableDiscounts] = useState(
    {} as RedeemableDiscounts
  );

  const addDiscountState = useSelector(addDiscountStateSelector);
  const clearDiscountsState = useSelector(clearDiscountsStateSelector);
  const fetchDiscountsState = useSelector(fetchDiscountsStateSelector);
  const removeDiscountState = useSelector(removeDiscountStateSelector);

  const history = useHistory();
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);

  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const hasCheckingOut = queryParams['checking_out'] === 'true';
  const firstRender = useRef(true);

  const clearDiscounts = () => {
    dispatch(discountsActions.clearDiscounts());
  };

  const removeCustomerFromSale = () => {
    if (typeof customer === 'undefined') {
      throw new Error(
        'Unexpected application state. Customer should not be undefined.'
      );
    } else if (!isCustomerAddedToSale) {
      throw new Error(
        'Unexpected application state. There is no customer to remove.'
      );
    }

    dispatch(customerActions.removeCustomerFromSale());
    clearDiscounts();
  };

  const addCustomerToSale = () => {
    if (typeof customer === 'undefined') {
      throw new Error(
        'Unexpected application state. Customer should not be undefined.'
      );
    } else if (isCustomerAddedToSale) {
      throw new Error(
        'Unexpected application state. Customer is already added.'
      );
    }

    dispatch(customerActions.addCustomerToSale(customer));
  };

  const back = () => {
    if (isCustomerAddedToSale) {
      setShowBackWarningModal(true);
    } else {
      dispatch(customerActions.resetCustomer());
      dispatch(discountsActions.resetDiscounts());

      // If we fetch customer through barcode scanner scan, we always go back
      // to the main page. If we fetch customer through the SDK, it means that
      // the customer was already added to the order and we automatically
      // routed to the customer page. Hence, go back to the main screen.
      if (
        customerContext === RequestContext.BARCODE_SCANNER ||
        customerContext === RequestContext.SDK
      ) {
        history.replace('/' + history.location.search);
      } else {
        history.goBack();
      }
    }
  };

  const dismissBackWarningModal = () => {
    setShowBackWarningModal(false);
  };

  const onClickContinueButton = useCallback(() => {
    view.continueClicked();
  }, []);

  const showConnectionErrorModal = () => {
    return (
      addCustomerToSaleState.error ||
      removeCustomerFromSaleState.error ||
      clearDiscountsState.error ||
      fetchDiscountsState.error
    );
  };

  const onClickTryAgain = () => {
    if (addCustomerToSaleState.error && customer) {
      dispatch(customerActions.addCustomerToSale(customer));
    }

    if (removeCustomerFromSaleState.error) {
      dispatch(customerActions.removeCustomerFromSale());
    }

    if (clearDiscountsState.error) {
      dispatch(discountsActions.clearDiscounts());
    }

    if (fetchDiscountsState.error) {
      dispatch(discountsActions.fetchDiscounts());
    }
  };

  useEffect(() => {
    if (firstRender.current && !fetchDiscountsState.success) {
      dispatch(discountsActions.fetchDiscounts());
      firstRender.current = false;
    }
  }, [dispatch, fetchDiscountsState.success]);

  useEffect(() => {
    if (updatedDiscounts.length > 0) {
      history.replace('/invalid-discounts' + history.location.search);
    }
  }, [history, updatedDiscounts]);

  useEffect(() => {
    const redeemableDiscounts = {} as RedeemableDiscounts;

    for (const appliedDiscount of appliedDiscounts) {
      const rewardTierId = appliedDiscount.metadata.rewardTierId;
      if (!redeemableDiscounts[rewardTierId]) {
        redeemableDiscounts[rewardTierId] = {
          availableDiscounts: [],
          appliedDiscounts: [],
        };
      }

      redeemableDiscounts[rewardTierId].appliedDiscounts.push(appliedDiscount);
    }

    for (const availableDiscount of availableDiscounts) {
      const rewardTierId = availableDiscount.metadata.rewardTierId;

      if (!redeemableDiscounts[rewardTierId]) {
        redeemableDiscounts[rewardTierId] = {
          availableDiscounts: [],
          appliedDiscounts: [],
        };
      }

      redeemableDiscounts[rewardTierId].availableDiscounts.push(
        availableDiscount
      );
    }

    setRedeemableDiscounts(redeemableDiscounts);
  }, [
    availableDiscounts,
    appliedDiscounts,
    addDiscountState,
    removeDiscountState,
    clearDiscountsState,
  ]);

  useEffect(() => {
    if (!hasCheckingOut) {
      return;
    }

    const eventListener = (e: any) => {
      if (e.key === 'Enter') {
        onClickContinueButton();
      }
    };

    window.addEventListener('keydown', eventListener);

    return () => {
      window.removeEventListener('keydown', eventListener);
    };
  }, [hasCheckingOut, onClickContinueButton]);

  if (fetchDiscountsState.isPending) {
    return <LoadingScreen />;
  }

  if (showConnectionErrorModal()) {
    return <ConnectionError onClickTryAgain={onClickTryAgain} />;
  }

  return (
    <div className="market-grid-container">
      {showBackWarningModal && (
        <BackWarningModal onDismiss={dismissBackWarningModal} />
      )}

      {(addCustomerToSaleState.isPending ||
        removeCustomerFromSaleState.isPending ||
        clearDiscountsState.isPending ||
        addDiscountState.isPending ||
        removeDiscountState.isPending) && <LoadingScreen transparent />}

      <MarketHeader className="market-grid-item-full addons-header" compact>
        <MarketButton
          data-testid="customer-back-btn"
          onClick={back}
          rank="secondary"
          slot="navigation"
        >
          <div slot="icon" className="addons-icon">
            <ArrowLeft className="addons-back-icon" width="24" height="24" />
          </div>
        </MarketButton>

        <div slot="actions" style={{ display: 'flex' }}>
          {isCustomerAddedToSale ? (
            <>
              <MarketDropdown
                slot="actions"
                popover-placement="bottom-end"
                style={{ marginRight: '15px' }}
              >
                <MarketButton
                  slot="trigger"
                  rank="secondary"
                  data-testid="ellipsis-btn"
                >
                  <div slot="icon" className="addons-icon">
                    <EllipsisHorizontal className="addons-ellipsis-icon" />
                  </div>
                </MarketButton>
                <MarketPopover slot="popover">
                  <MarketList transient>
                    <MarketRow
                      onClick={removeCustomerFromSale}
                      data-testid="remove-from-sale-popover-row"
                    >
                      Remove from sale
                    </MarketRow>
                  </MarketList>
                </MarketPopover>
              </MarketDropdown>
              {hasCheckingOut ? (
                <MarketButton
                  data-testid="customer-continue-btn"
                  onClick={onClickContinueButton}
                  rank="primary"
                  slot="actions"
                >
                  Continue
                </MarketButton>
              ) : (
                <MarketButton
                  data-testid="customer-done-btn"
                  onClick={onClickContinueButton}
                  rank="primary"
                >
                  Done
                </MarketButton>
              )}
            </>
          ) : (
            <MarketButton
              data-testid="add-customer-to-sale-btn"
              onClick={onClickContinueButton}
              rank="primary"
            >
              Continue
            </MarketButton>
          )}
        </div>
      </MarketHeader>

      <MarketList
        className="market-grid-item-full"
        data-testid="customer-information"
      >
        <div>
          <h2>{getCustomerDisplayName(customer)}</h2>
        </div>

        {pointBalance && pointBalance > 0 ? (
          <div style={{ marginBottom: '48px' }}>
            <p>{pointBalance} points total</p>
          </div>
        ) : (
          <></>
        )}

        {Object.keys(redeemableDiscounts).length > 0 ? (
          <div>
            <h3>Available rewards</h3>
          </div>
        ) : (
          <div>
            <h3>No rewards found</h3>
          </div>
        )}

        {Object.keys(redeemableDiscounts).map((rewardTierId: string) => {
          const { availableDiscounts, appliedDiscounts } =
            redeemableDiscounts[rewardTierId];

          return (
            <RedeemableDiscountCard
              key={rewardTierId}
              rewardTierId={rewardTierId}
              availableDiscounts={availableDiscounts}
              appliedDiscounts={appliedDiscounts}
              isCustomerAddedToSale={isCustomerAddedToSale}
              customer={customer}
            />
          );
        })}

        {unavailableDiscounts.length > 0 ? (
          <div style={{ marginTop: '32px' }}>
            <h3>Unavailable rewards</h3>
          </div>
        ) : (
          <></>
        )}

        {unavailableDiscounts.map((unavailableDiscount) => {
          return (
            <MarketContentCard
              className="addons-unavailable-rewards-content-card"
              key={unavailableDiscount}
            >
              <MarketRow data-testid={`${unavailableDiscount}-card`}>
                <label slot="label">{unavailableDiscount}</label>
                <MarketButton slot="control" rank="secondary" disabled>
                  Redeem
                </MarketButton>
              </MarketRow>
            </MarketContentCard>
          );
        })}
      </MarketList>
    </div>
  );
};

export default CustomerScreen;
